import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

const Projects = ({ projects, autoPlayInterval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const navigate = useNavigate();

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % projects.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + projects.length) % projects.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handleProjectClick = (project) => {
    navigate(`/projects/${project.route}`);
  };

  const handleViewAllProjects = () => {
    navigate('/projects');
  };

  useEffect(() => {
    let interval;
    if (isAutoPlaying && projects.length > 1) {
      interval = setInterval(nextSlide, autoPlayInterval);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, projects.length, autoPlayInterval]);

  if (!projects || projects.length === 0) {
    return null;
  }

  const currentProject = projects[currentIndex];

  return (
    <div className="w-full bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header Section with Glass Effect */}
      <div className="relative overflow-hidden">
        {/* Animated Background Pattern - Green Theme */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-grid-emerald-500/20 bg-[size:50px_50px]" />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-600/10 animate-pulse" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
          {/* Glass Card for Header - Light Theme */}
          <div className="backdrop-blur-md bg-white/80 rounded-2xl p-8 border border-emerald-100 shadow-xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-center text-gray-800 mb-4 font-['Poppins','Inter',sans-serif] tracking-tight">
              Our <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Projects</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mb-6 rounded-full" />
            <p className="text-lg md:text-xl text-center text-gray-600 max-w-3xl mx-auto font-['Inter',sans-serif] leading-relaxed">
              Discover our exceptional portfolio of premium construction projects, 
              showcasing innovation, quality, and excellence across Pakistan
            </p>
          </div>
        </div>
      </div>

      {/* Carousel Section - Full Width */}
      <div className="w-[95%] mx-auto relative rounded-2xl overflow-hidden shadow-2xl">
        <div 
          className="relative group"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {/* Main Content */}
          <div className="relative h-[550px] md:h-[650px] lg:h-[750px] overflow-hidden">
            {/* Background Image with Parallax Effect */}
            <div 
              className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110"
              style={{ backgroundImage: `url(${currentProject.image})` }}
            >
              {/* Enhanced Gradient Overlays - Light/Green Theme */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/30 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white/70" />
              
              {/* Subtle Green Overlay */}
              <div className="absolute inset-0 bg-emerald-500/5" />
            </div>

            {/* Content */}
            <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
              <div className="max-w-2xl text-gray-800 animate-fadeIn">
                {/* Featured Badge with Glass Effect - Green Theme */}
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 backdrop-blur-md bg-emerald-100/80 px-4 py-2 rounded-full border border-emerald-300 shadow-md">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <span className="text-sm font-semibold uppercase tracking-wider text-emerald-700">
                      Featured Project
                    </span>
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 font-['Poppins','Inter',sans-serif] leading-tight text-gray-800">
                  {currentProject.title}
                </h2>

                {/* Location */}
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-lg md:text-xl text-gray-600 font-['Inter',sans-serif]">
                    {currentProject.location}
                  </p>
                </div>

                {/* Description */}
                <p className="text-base md:text-lg text-gray-600 mb-8 line-clamp-3 font-['Inter',sans-serif] leading-relaxed">
                  {currentProject.description}
                </p>

                {/* Buttons with Glass Effect - Green Theme */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => handleProjectClick(currentProject)}
                    className="group relative inline-flex items-center justify-center px-8 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-all duration-300 text-white font-semibold text-lg overflow-hidden shadow-lg hover:shadow-emerald-500/25 transform hover:-translate-y-0.5"
                  >
                    <span className="relative z-10 flex items-center">
                      View Project Details
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </button>
                  
                  <button
                    onClick={handleViewAllProjects}
                    className="group relative inline-flex items-center justify-center px-8 py-3 backdrop-blur-md bg-white/80 hover:bg-white/90 rounded-xl transition-all duration-300 text-emerald-700 font-semibold text-lg border border-emerald-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center">
                      View All Projects
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Navigation Arrows with Glass Effect - Green Theme */}
            {projects.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-6 top-1/2 -translate-y-1/2 backdrop-blur-md bg-white/80 hover:bg-white text-gray-700 p-4 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 border border-emerald-200 shadow-xl"
                  aria-label="Previous project"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-6 top-1/2 -translate-y-1/2 backdrop-blur-md bg-white/80 hover:bg-white text-gray-700 p-4 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 border border-emerald-200 shadow-xl"
                  aria-label="Next project"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>

          {/* Dots Indicator with Glass Effect - Green Theme */}
          {projects.length > 1 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3">
              {projects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex
                      ? 'bg-emerald-500 w-10 h-2 shadow-lg shadow-emerald-500/30'
                      : 'bg-gray-300 hover:bg-emerald-300 w-2 h-2'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Counter Badge with Glass Effect - Green Theme */}
          {projects.length > 1 && (
            <div className="absolute bottom-8 right-8 backdrop-blur-md bg-white/80 px-4 py-2 rounded-full text-gray-700 text-sm font-medium border border-emerald-200 shadow-lg">
              <span className="text-emerald-600 font-bold">{currentIndex + 1}</span> / {projects.length}
            </div>
          )}
        </div>
      </div>

      {/* Add custom CSS for animations and fonts */}
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&display=swap');
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
        
        .bg-grid-emerald-500\/20 {
          background-image: linear-gradient(to right, rgba(16, 185, 129, 0.2) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(16, 185, 129, 0.2) 1px, transparent 1px);
        }
      `}</style>
    </div>
  );
};

export default Projects;